import React from "react"
import { TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "material-ui/Table"
import Table from "../common/Table"
import TableActionBar from "../../components/layout/TableActionBar"
import strings from "../../localization/strings"
import FloatingActionButton from "material-ui/FloatingActionButton"
import ContentAdd from "material-ui/svg-icons/content/add"
import autobind from "class-autobind"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import AddOTPolicyDialog from "./AddOTPolicyDialog"
import { EOTType, EOvertimeRuleType, IOTPolicy, IOvertimeRule } from "../../models"
import _ from "lodash"

interface IProps {
    otPolicies: IOTPolicy[]
    addOTPolicy(otPolicy: IOTPolicy, cb: Function): void
    updateOTPolicy(editOTPolicy: IOTPolicy, cb: Function): void
    deleteOTPolicy(deleteOTPolicyId: string, cb: Function): void
}
interface IState {
    openAddOTPolicyDialog: boolean
    selectedOTPolicy: IOTPolicy
    editOTPolicy: IOTPolicy
}

export default class OTPolicies extends React.Component<IProps, IState>  {
    constructor(props: IProps) {
        super(props)
        this.state = {
            openAddOTPolicyDialog: false,
            selectedOTPolicy: null,
            editOTPolicy: null
        }
        autobind(this)
    }

    getOTPolicyTypeLabel(type: EOTType) {
        switch (type) {
            case EOTType.Weekly:
                return strings.Weekly
            case EOTType.Bi_Weekly:
                return strings.Bi_Weekly
        }
    }

    getOTThreshold(overtimeRule: IOvertimeRule) {
        return overtimeRule && overtimeRule.OvertimeHourThreshold ? overtimeRule.OvertimeHourThreshold : ""
    }

    getOT2Enabled(overtimeRule: IOvertimeRule) {
        return overtimeRule ? overtimeRule.EnableDoubleOvertime : false
    }

    getOT2Threshold(overtimeRule: IOvertimeRule) {
        return overtimeRule && overtimeRule.DoubleOvertimeHourThreshold ? overtimeRule.DoubleOvertimeHourThreshold : ""
    }

    getConsecutiveDay(overtimeRule: IOvertimeRule) {
        return overtimeRule && overtimeRule.ConsecutiveDaysSettings && overtimeRule.ConsecutiveDaysSettings.DaysWorkedToTriggerOvertime ?
            overtimeRule.ConsecutiveDaysSettings.DaysWorkedToTriggerOvertime :
            ""
    }

    render() {
        const hideOT2 = AccountStore.getHideOT2()
        return (
            <div>
                <FloatingActionButton
                    data-intercom-target={"menu-AccountSettings-PayConfiguration-OTPoliciesTab-Btn-Add"}
                    className="fixed-add"
                    style={{ top: -28, right: 20 }}
                    secondary={true}
                    onTouchTap={() => this.setState({ openAddOTPolicyDialog: true })}
                >
                    <ContentAdd />
                </FloatingActionButton>
                <div style={{ paddingTop: 30 }}>
                    <TableActionBar
                        searchText=""
                        rowSelectionCount={this.state.selectedOTPolicy ? 1 : 0}
                        singleSelectedMessage={strings.OT_Policy_Selected}
                        title={strings.OT_Policies}
                        titleStyle={{ fontSize: 16 }}
                        bodyStyle={{ padding: 20, fontWeight: 500 }}
                        onDeleteClick={() => AppStateStore.setConfirmDialogContent({
                            content: strings.Are_you_sure_you_want_to_delete_OT_policy,
                            confirm: () => {
                                AppStateStore.setProgressSnackbarContent("loading")
                                this.props.deleteOTPolicy(this.state.selectedOTPolicy.Id, () =>
                                    AppStateStore.setProgressSnackbarContent(strings.Successfully_deleted)
                                )
                            }
                        })}
                    />
                    <div style={{ maxHeight: 250, overflowY: "auto" }}>
                        {this.props.otPolicies.length === 0 ?
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>{strings.No_OT_Policy}</h2> :
                            <Table
                                items={this.props.otPolicies}
                                singleSelectable
                                handleClickRow={editOTPolicy => this.setState({ openAddOTPolicyDialog: true, editOTPolicy })}
                                handleSelect={selectedOTPolicies => this.setState({ selectedOTPolicy: selectedOTPolicies.length ? selectedOTPolicies[0] : null })}
                            >
                                <TableHeader>
                                    <TableRow className="ShortSelectBoxTableHeader">
                                        <TableHeaderColumn style={styles.normalCol}>{strings.Name}</TableHeaderColumn>
                                        <TableHeaderColumn style={styles.normalCol}>{strings.Type}</TableHeaderColumn>
                                        <TableHeaderColumn style={styles.numberCol}>{strings.Daily_OT}</TableHeaderColumn>
                                        {!hideOT2 &&
                                            <TableHeaderColumn style={styles.numberCol}>{strings.Daily_OT2}</TableHeaderColumn>
                                        }
                                        <TableHeaderColumn style={styles.numberCol}>{strings.Weekly_OT}</TableHeaderColumn>
                                        {!hideOT2 &&
                                            <TableHeaderColumn style={styles.numberCol}>{strings.Weekly_OT2}</TableHeaderColumn>
                                        }
                                        <TableHeaderColumn style={styles.numberCol}>{strings.Bi_Weekly_OT}</TableHeaderColumn>
                                        {!hideOT2 &&
                                            <TableHeaderColumn style={styles.numberCol}>{strings.Bi_Weekly_OT2}</TableHeaderColumn>
                                        }
                                        <TableHeaderColumn style={{ ...styles.numberCol, width: 120 }}>{strings.ConsecutiveRule_OT}</TableHeaderColumn>
                                        {!hideOT2 &&
                                            <TableHeaderColumn style={{ ...styles.numberCol, width: 120 }}>{strings.ConsecutiveRule_OT2}</TableHeaderColumn>
                                        }
                                        <TableHeaderColumn style={{ ...styles.numberCol, width: 120 }}>{strings.ConsecutiveRule_Days}</TableHeaderColumn>
                                    </TableRow>
                                </TableHeader>
                                <TableBody deselectOnClickaway={false}>
                                    {this.props.otPolicies.map((otPolicy, index) => {
                                        const dailyRule = otPolicy.OvertimeRules.find(i => i.Type === EOvertimeRuleType.Daily)
                                        const weeklyRule = otPolicy.OvertimeRules.find(i => i.Type === EOvertimeRuleType.Weekly)
                                        const consecutiveRule = otPolicy.OvertimeRules.find(i => i.Type === EOvertimeRuleType.ConsecutiveDay)
                                        const biWeeklyRule = otPolicy.OvertimeRules.find(i => i.Type === EOvertimeRuleType.Bi_Weekly)
                                        return (
                                            <TableRow
                                                key={index}
                                                className="ShortSelectBoxTableRow"
                                                data-intercom-target={index === 0 && "menu-AccountSettings-PayConfiguration-OTPoliciesTab-openItemDetails"}
                                                selected={this.state.selectedOTPolicy ? otPolicy.Id === this.state.selectedOTPolicy.Id : false}
                                            >
                                                <TableRowColumn style={styles.normalCol}>{otPolicy.Name}</TableRowColumn>
                                                <TableRowColumn style={styles.normalCol}>{this.getOTPolicyTypeLabel(otPolicy.Type)}</TableRowColumn>
                                                <TableRowColumn style={styles.numberCol}>{this.getOTThreshold(dailyRule)}</TableRowColumn>
                                                {!hideOT2 &&
                                                    <TableRowColumn style={styles.numberCol}>{this.getOT2Threshold(dailyRule)}</TableRowColumn>
                                                }
                                                <TableRowColumn style={styles.numberCol}>{this.getOTThreshold(weeklyRule)}</TableRowColumn>
                                                {!hideOT2 &&
                                                    <TableRowColumn style={styles.numberCol}>{this.getOT2Threshold(weeklyRule)}</TableRowColumn>
                                                }
                                                <TableRowColumn style={styles.numberCol}>{this.getOTThreshold(biWeeklyRule)}</TableRowColumn>
                                                {!hideOT2 &&
                                                    <TableRowColumn style={styles.numberCol}>{this.getOT2Threshold(biWeeklyRule)}</TableRowColumn>
                                                }
                                                <TableRowColumn style={{ ...styles.numberCol, width: 120 }}>{this.getOTThreshold(consecutiveRule)}</TableRowColumn>
                                                {!hideOT2 &&
                                                    <TableRowColumn style={{ ...styles.numberCol, width: 120 }}>{this.getOT2Threshold(consecutiveRule)}</TableRowColumn>
                                                }
                                                <TableRowColumn style={{ ...styles.numberCol, width: 120 }}>{this.getConsecutiveDay(consecutiveRule)}</TableRowColumn>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        }
                    </div>
                </div>
                {this.state.openAddOTPolicyDialog &&
                    <AddOTPolicyDialog
                        editOTPolicy={this.state.editOTPolicy}
                        otPolicies={this.props.otPolicies}
                        handleAdd={otPolicy => {
                            this.setState({ openAddOTPolicyDialog: false, editOTPolicy: null })
                            AppStateStore.setProgressSnackbarContent("loading")
                            this.props.addOTPolicy(otPolicy, () => AppStateStore.setProgressSnackbarContent(strings.OT_Policy_created_successfully))
                        }}
                        handleUpdate={otPolicy => {
                            this.setState({ openAddOTPolicyDialog: false, editOTPolicy: null })
                            AppStateStore.setProgressSnackbarContent("loading")
                            this.props.updateOTPolicy(otPolicy, () => AppStateStore.setProgressSnackbarContent(strings.OT_Policy_updated_successfully))
                        }}
                        handleClose={() => this.setState({ openAddOTPolicyDialog: false, editOTPolicy: null })}
                    />
                }
            </div>
        )
    }
}

const styles = {
    normalCol: {
        paddingLeft: 5,
        paddingRight: 5
    },
    numberCol: {
        paddingLeft: 5,
        paddingRight: 5,
        textAlign: "center"
    }
}